import { useState } from 'react';
import {
  formatSpelledPitch,
  midiOf,
  STEPS,
  type SpelledPitch,
  type Step,
} from '@/music/score/pitch';

/** Written pitches share the staff's spelling and transposition. */
export function ScoreNoteheadControls({
  heads,
  selectedIndex,
  onSelect,
  onAdd,
  onRemove,
}: {
  heads: readonly SpelledPitch[];
  selectedIndex: number;
  onSelect: (index: number) => void;
  onAdd: (pitch: SpelledPitch) => void;
  onRemove: () => void;
}) {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState<SpelledPitch>({ step: 'E', alter: 0, octave: 4 });
  const duplicate = heads.some((head) => formatSpelledPitch(head) === formatSpelledPitch(draft));
  const valid = midiOf(draft) >= 0 && midiOf(draft) <= 127 && !duplicate;
  return (
    <div className="score-notehead-controls" role="group" aria-label="Chord notes">
      {heads.length > 1 && (
        <select
          aria-label="Note in chord"
          value={selectedIndex}
          onChange={(event) => onSelect(Number(event.target.value))}
        >
          {heads.map((head, index) => (
            <option key={index} value={index}>
              {formatSpelledPitch(head)}
            </option>
          ))}
        </select>
      )}
      <button
        type="button"
        className="btn btn-sm"
        onClick={() => {
          const current = heads[selectedIndex] ?? heads[0];
          const index = STEPS.indexOf(current.step) + 2;
          setDraft({
            step: STEPS[index % 7],
            alter: 0,
            octave: Math.min(8, current.octave + Math.floor(index / 7)),
          });
          setAdding(true);
        }}
      >
        Add note
      </button>
      <button
        type="button"
        className="btn btn-sm"
        onClick={onRemove}
        title={
          heads.length === 1
            ? 'Replace the last note with a rest'
            : 'Remove the selected note from the chord'
        }
      >
        Remove note
      </button>
      {adding && (
        <form
          className="score-notehead-entry"
          onSubmit={(event) => {
            event.preventDefault();
            if (!valid) return;
            onAdd(draft);
            setAdding(false);
          }}
        >
          <select
            aria-label="New note letter"
            value={draft.step}
            onChange={(event) => setDraft({ ...draft, step: event.target.value as Step })}
          >
            {STEPS.map((step) => (
              <option key={step}>{step}</option>
            ))}
          </select>
          <select
            aria-label="New note accidental"
            value={draft.alter}
            onChange={(event) => setDraft({ ...draft, alter: Number(event.target.value) })}
          >
            {[
              [-2, '♭♭'],
              [-1, '♭'],
              [0, '♮'],
              [1, '♯'],
              [2, '𝄪'],
            ].map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
          <select
            aria-label="New note octave"
            value={draft.octave}
            onChange={(event) => setDraft({ ...draft, octave: Number(event.target.value) })}
          >
            {Array.from({ length: 9 }, (_, octave) => (
              <option key={octave}>{octave}</option>
            ))}
          </select>
          <button type="submit" className="btn btn-sm" disabled={!valid}>
            Add to chord
          </button>
          <button type="button" className="btn btn-sm" onClick={() => setAdding(false)}>
            Cancel
          </button>
          {!valid && (
            <span role="status">
              {duplicate
                ? 'That note is already in the chord.'
                : 'Choose a pitch within MIDI range.'}
            </span>
          )}
        </form>
      )}
    </div>
  );
}
